import React, { useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/store';
import { createSurvey, resetSurvey } from '../store/features/CreateSurveySlice';
import Modal from '../Components/Modal';
import { questionTypesArray, Survey } from '../types';
import TextField from '../Components/TextField';
import { generateSurvey, updateSurveyDescription } from '../Firebase/utils';
import MultipleChoiceForm from '../Components/MultipleChoiceForm';
import RatingForm from '../Components/RatingForm';
import ScaleForm from '../Components/ScaleForm';
import YesNoForm from '../Components/YesNoForm';
import ShortResponseForm from '../Components/ShortResponseForm';
import QuestionPreviewDisplay from '../Components/QuestionPreviewDisplay';
import MultipleSelectionForm from '../Components/MultipleSelectionForm';
import DropDownSelectionForm from '../Components/DropDownSelectionForm';
import { BsClipboard2CheckFill, BsClipboard2Fill } from "react-icons/bs";
import { FaChartBar, FaRegSave } from 'react-icons/fa';
import { useMutation } from '@tanstack/react-query';

const CreateSurvey = () => {

    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const user = useAppSelector(state => state.user.user);
    const reduxSurvey = useAppSelector(state => state.survey.survey);
    const [ survey, setSurvey ] = useState<Survey>(reduxSurvey);
    const [ isModalOpen, setIsModalOpen ] = useState(false);
    const [ isQuestionModalOpen, setIsQuestionModalOpen ] = useState<boolean>(false);
    const [ questionType, setQuestionType ] = useState('');
    const [ renderForm, setRenderForm ] = useState<JSX.Element | null>(null);
    const [ copied, setCopied ] = useState<boolean>(false);
    const [ fullTitle, setFullTitle ] = useState<boolean>(false);
    const [ description, setDescription ] = useState<string>('');

    useEffect(() => {
        if(survey.title === ''){
          setIsModalOpen(true);
        }
    }, []);

    useEffect(() => {
        return () => {
          setSurvey(reduxSurvey);
        }
    }, []);

    const setTitle = (title: string) => {
      console.log(title);
        setSurvey({...survey, title: title});
    }

    const setDescriptionText = (description: string) => {
        setSurvey({...survey, description: description});
    }

    const handleCloseModal = () => {
        setIsModalOpen(false);
        navigate('/');
    }

    const handleGenerateSurvey = async () => {
        console.log('Generating Survey...', survey);
        try{
            const newSurvey = await generateSurvey(user, survey);
            console.log(newSurvey);
            if(newSurvey){
              dispatch(createSurvey(newSurvey));
              setSurvey(newSurvey);
            }
            setIsModalOpen(false);
        }catch(err){
            console.log(err);
        }
    }

    console.log('Survey:', survey);

    useEffect(() => {
        if(questionType !== ''){
            setRenderForm(handleQuestionType(questionType));
        }
    }, [questionType]);

    const handleQuestionType = (type: string) => {
        setIsQuestionModalOpen(false);
        switch(type){
            case 'Multiple Choice':
                return <MultipleChoiceForm openState={questionType} onClose={setQuestionType}/>;
            case 'Multiple Selection':
                return <MultipleSelectionForm openState={questionType} onClose={setQuestionType}/>;
            case 'Dropdown Selection':
                return <DropDownSelectionForm openState={questionType} onClose={setQuestionType}/>;
            case 'Short Response':
                return <ShortResponseForm openState={questionType} onClose={setQuestionType}/>;
            case 'Scale':
                return <ScaleForm openState={questionType !== ''} onClose={setQuestionType}/>;
            case 'Rating':
                return <RatingForm openState={questionType !== ''} onClose={setQuestionType}/>;
            case 'Yes/No':
                return <YesNoForm openState={questionType !== ''} onClose={setQuestionType}/>;
            default:
                return null;
        }
    }

    const handleCopyLink = () => {
        if(!reduxSurvey.questions?.length){
            alert('Please add questions to your quiz before sharing');
            return;
        }
        const link = `https://surveym3.com/complete-survey/${survey.id}`;
        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(link).then(() => {
                setCopied(true);
            }).catch(err => {
                console.error('Failed to copy: ', err);
            });
        } else {
            // Fallback for browsers that do not support navigator.clipboard
            const textArea = document.createElement("textarea");
            textArea.value = link;
            document.body.appendChild(textArea);
            textArea.select();
            try {
                document.execCommand('copy');
                setCopied(true);
            } catch (err) {
                console.error('Fallback: Oops, unable to copy', err);
            }
            document.body.removeChild(textArea);
        }
    };

    const saveSurveyMutation = useMutation({
        mutationFn: async () => {
            try {
                const newSurvey = await updateSurveyDescription(reduxSurvey, survey?.description ?? '');
                console.log(newSurvey);
            } catch (error) {
                console.error('Error saving survey:', error);
            }
        },
        onSuccess: () => {
            alert('Survey saved successfully');
            // dispatch(resetSurvey());
            navigate(`/analyze-results/${survey.id}`)
        },
        onError: (error) => {
            console.error('Error saving survey:', error);
        }
    });

    return (
        <div className='flex flex-col max-w-full min-h-full'>
          <div className='flex flex-col md:flex-row md:justify-between'>
            <button onClick={() => {navigate(`/analyze-results/${survey.id}`)}} className='self-center md:self-start gap-3 ml-7 max-w-full md:w-fit mt-5 mr-10 px-4 py-2 text-2xl text-[#3da8e2] border border-[#3da8e2] rounded-xl font-BardotBoldItalic'>
              <p className='flex justify-center items-center gap-4'><FaChartBar />Analyze Results</p>
            </button>
            <button onClick={() => {saveSurveyMutation.mutate();}} className='self-center md:self-end gap-3 ml-7 max-w-full md:w-fit mt-5 mr-10 px-4 py-2 text-2xl text-green-400 border border-green-400 rounded-xl font-BardotBoldItalic'>
              <p className='flex justify-center items-center gap-4'><FaRegSave />Save</p>
            </button>
          </div>
        <header className='flex flex-col md:flex-row justify-center items-center gap-3 md:gap-10 text-[#3da8e2] text-center p-4 pt-10 md:pt-20'>
            <div className='flex flex-col gap-4 w-full'>
                {isModalOpen ? <h1 className='text-[2.7997rem] md:text-8xl font-Velocity tracking-widest text-shadow-lg'>Create a Survey</h1>
                :
                <div>
                  <div className='overflow-hidden'>
                    <h1 className={`text-lg md:text-5xl font-Velocity tracking-widest text-shadow-lg ${fullTitle ? 'break-words' : 'overflow-hidden whitespace-nowrap text-ellipsis'}`}>{reduxSurvey.title}</h1>
                    <button onClick={() => {setFullTitle(!fullTitle)}} className='text-[#fff] font-BardotBold tracking-widest'>{fullTitle ? 'Collapse' : 'Expand'} Title</button>
                  </div>
                  <div className='flex items-center justify-center overflow-hidden'>
                    <TextField
                      placeholder='Survey Description (Optional)'
                      inputValue={survey.description ?? ''}
                      setInputValue={(text) => setDescriptionText(text)}
                      styles={'w-full md:w-1/2 mt-4'}
                      type={'text'}
                      multiline
                      minRows={3}
                      maxRows={6}
                      />
                    {/* <h1 className={`text-lg md:text-5xl font-Velocity tracking-widest text-shadow-lg ${description ? 'break-words' : 'overflow-hidden whitespace-nowrap text-ellipsis'}`}>{reduxSurvey.description}</h1>
                    <button onClick={() => {setFullTitle(!fullTitle)}} className='text-[#fff] font-BardotBold tracking-widest'>{fullTitle ? 'Collapse' : 'Expand'} Description</button> */}
                  </div>
                </div>
                }
                <div className={'self-center w-fit max-w-full'} onClick={handleCopyLink} style={{ cursor:`${reduxSurvey.questions?.length >= 1 ? 'pointer' : 'not-allowed'}` }}>
                    <div className='border rounded-lg'>
                        {copied ? 
                            <div className='flex items-center p-2'>
                                <BsClipboard2CheckFill className='text-2xl md:text-2xl text-green-400 mx-4'/>
                                <p className='md:text-xl text-green-400 font-bold md:line-clamp-1 overflow-hidden whitespace-nowrap text-ellipsis'>{`https://surveym3.com/complete-survey/${reduxSurvey.id}`}</p>
                            </div>
                            :
                            <div className='flex items-center p-2'>
                                <BsClipboard2Fill className='text-2xl md:text-2xl text-slate-400 mx-4'/>
                                <p className='md:text-xl text-slate-400 font-bold md:line-clamp-1 overflow-hidden whitespace-nowrap text-ellipsis'>{`https://surveym3.com/complete-survey/${reduxSurvey.id}`}</p>
                            </div>
                        }
                    </div>
                    <p className='text-center text-slate-300 font-bold tracking-widest'>{copied ? 'COPIED!' : 'Copy Link to Share'}</p>
                </div>
            </div>
        </header>
            {!isModalOpen && <button onClick={() => setIsQuestionModalOpen(true)} className='self-center w-11/12 md:w-1/2 bg-[#3da8e2] text-white p-4 rounded-md border-[#8dd2f6] border shadow-black shadow-lg font-BardotBold tracking-widest'>Add Question</button>}
            <section className='flex flex-col gap-10 items-center mt-10 text-white font-BCBold'>
                {reduxSurvey.questions?.map((question, index) => {
                    return(
                        <QuestionPreviewDisplay key={index} question={question} />
                    );
                })
                }
            </section>
            <Modal title="Create New Survey" openState={isModalOpen} onClose={handleCloseModal} styles={'min-h-10'}>
                <div className='flex flex-col gap-5 p-5'>
                    <h1 className='text-3xl font-Velocity'>Let's Start With a Title...</h1>
                    <TextField placeholder='Survey Title' inputValue={survey.title} setInputValue={(text) => setTitle(text)} />
                    <button onClick={() => {handleGenerateSurvey()}} className='w-full bg-[#3da8e2] text-white p-2 rounded-md border-[#8dd2f6] border shadow-black shadow-lg font-BardotBold tracking-widest'>Generate Survey</button>
                </div>
            </Modal>
            <Modal title="Select A Question Type" openState={isQuestionModalOpen} onClose={() => setIsQuestionModalOpen(false)} styles={'min-h-10'}>
                <div className='flex flex-col gap-5 p-5'>
                    {questionTypesArray.map((type: string) => {
                        return(
                            <button onClick={() => {setQuestionType(type)}} className='bg-[#3da8e2] text-white p-2 rounded-md border-[#8dd2f6] border shadow-black shadow-lg font-BardotBold tracking-widest'>{type}</button>
                        );
                    }
                    )}
                </div>
            </Modal>
            {renderForm}
        </div>
    );
}

export default CreateSurvey;